import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import ReactGA from 'react-ga4';
import { categories } from './data';
import './App.css';

// Initialize GA4
ReactGA.initialize('G-GG55KQKV4Z');

// Group segments by category for the legend and specify order
const legendOrder = ['wholesale', 'misc', 'network', 'renewables'];
const legendGroups = legendOrder.map(id => ({
  id,
  name: categories[id].name,
  color: categories[id].color
}));

function Dashboard() {
  const [activeSegment, setActiveSegment] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [defaultSegment, setDefaultSegment] = useState(null);
  const [defaultCategoryId, setDefaultCategoryId] = useState(null);
  const [selectedYear, setSelectedYear] = useState('26 FYTD');
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [showBenchmarkInfo, setShowBenchmarkInfo] = useState(false);

  const yearsContainerRef = React.useRef(null);
  const chartContainerRef = React.useRef(null);
  // Effect for chart container width
  React.useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.offsetWidth);
      }
      setIsMobile(window.innerWidth <= 600);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const getChartDimensions = () => {
    const baseOuterRadius = 180;
    const minRadius = 100;
    const scaleFactor = Math.min(containerWidth / 400, 1); // 400 is the "base" width
    const outerRadius = Math.max(baseOuterRadius * scaleFactor, minRadius);
    const innerRadius = outerRadius * 0.67; // maintain ratio between inner and outer radius

    return {
      innerRadius,
      outerRadius
    };
  };

  // Get all available years from any category's timeSeries
  const availableYears = Object.values(categories)[0].timeSeries.years;

  const handleSegmentHover = (data) => {
    if (data && data.payload) {
      setActiveSegment(data.payload);
      setActiveCategoryId(data.payload.categoryId);
    } else {
      setActiveSegment(null);
      setActiveCategoryId(null);
    }
  };

  const handleSegmentClick = (data) => {
    if (defaultSegment && defaultSegment.id === data.payload.id) {
      setDefaultSegment(null);
      setDefaultCategoryId(null);
      ReactGA.event({
        category: 'Segment Interaction',
        action: 'Unselect Segment',
        label: data.payload.name
      });
    } else {
      setDefaultSegment(data.payload);
      setDefaultCategoryId(data.payload.categoryId);
      ReactGA.event({
        category: 'Segment Interaction',
        action: 'Select Segment',
        label: data.payload.name
      });
    }
  };

  const handleBackgroundClick = () => {
    setDefaultSegment(null);
    setDefaultCategoryId(null);
    setActiveSegment(null);
    setActiveCategoryId(null);
  };

  const handleLegendHover = (categoryId) => {
    setActiveCategoryId(categoryId);
    setActiveSegment(null);
    if (categoryId) {
      ReactGA.event({
        category: 'Legend Interaction',
        action: 'Hover Legend',
        label: categories[categoryId].name
      });
    }
  };

  const handleYearChange = (direction) => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (direction === 'prev' && currentIndex > 0) {
      const newYear = availableYears[currentIndex - 1];
      setSelectedYear(newYear);
      ReactGA.event({
        category: 'Year Navigation',
        action: 'Previous Year',
        label: newYear
      });
    } else if (direction === 'next' && currentIndex < availableYears.length - 1) {
      const newYear = availableYears[currentIndex + 1];
      setSelectedYear(newYear);
      ReactGA.event({
        category: 'Year Navigation',
        action: 'Next Year',
        label: newYear
      });
    }
  };

  // Scroll to center the selected year
  React.useEffect(() => {
    if (yearsContainerRef.current) {
      const container = yearsContainerRef.current;
      const activeItem = container.querySelector('.year-item.active');
      if (activeItem) {
        const containerWidth = container.offsetWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.offsetWidth;
        const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2) - 40;
        // Only use smooth scrolling for user interactions, not initial load
        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: selectedYear === 'FY 24-25' ? 'auto' : 'smooth'
        });
      }
    }
  }, [selectedYear]);

  // Update getValueForYear to handle missing data
  const getValueForYear = (timeSeries) => {
    const yearIndex = timeSeries.years.indexOf(selectedYear);
    return yearIndex >= 0 ? (timeSeries.data[yearIndex] || 0) : 0;
  };

  // Update pieData to filter out entries with zero or invalid values
  const pieData = Object.entries(categories).flatMap(([categoryId, category]) => {
    if (!category.subcategories) {
      const value = getValueForYear(category.timeSeries);
      return value > 0 ? [{
        id: categoryId,
        categoryId,
        name: category.name,
        subtitle: category.subtitle,
        value,
        color: category.color,
        description: category.description,
        timeSeries: category.timeSeries
      }] : [];
    }

    return Object.entries(category.subcategories)
      .map(([subId, sub]) => {
        const value = getValueForYear(sub.timeSeries);
        return value > 0 ? {
          id: `${categoryId}-${subId}`,
          categoryId,
          name: sub.name,
          subtitle: sub.subtitle,
          value,
          color: category.color,
          description: sub.description,
          timeSeries: sub.timeSeries
        } : null;
      })
      .filter(entry => entry !== null);
  });

  const renderHistoricalChart = () => {
    const displaySegment = activeSegment || defaultSegment;
    if (!displaySegment) return null;

    // Labels are "FY 15-16" for complete years and "26 FYTD" for the running one,
    // so take the two digits from wherever that format puts them.
    const chartData = displaySegment.timeSeries.years.map((year, index) => ({
      year: year.startsWith("FY ") ? year.slice(3, 5) : year.slice(0, 2),
      value: displaySegment.timeSeries.data[index],
      fullYear: year
    }));

    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        return (
          <div className="chart-tooltip">
            <p>{payload[0].payload.fullYear}</p>
            <p>£{Math.round(payload[0].value)}</p>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="historical-chart">
        <h3>Historical bill cost</h3>
        <ResponsiveContainer width="100%" height={210}>
          <AreaChart data={chartData} margin={{ top: 10, right: 15, bottom: 20, left: 10 }}>
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1460F9" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#1460F9" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={false} />
            <XAxis
              dataKey="year"
              angle={0}
              interval={0}
              fontSize={12}
              tickFormatter={(value) => `'${value}`}
              label={{ value: 'Financial Year', position: 'bottom', offset: 10, fontSize: 14 }}
            />
            <YAxis
              tickFormatter={(value) => `£${value}`}
              width={45}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#1460F9"
              strokeWidth={2.5}
              fill="url(#colorUv)"
              dot={false}
              activeDot={{ r: 6 }}
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={500}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Calculate total bill and rate
  const totalBill = pieData.reduce((sum, item) => sum + item.value, 0);
  const ratePerKwh = Math.floor((totalBill * 100) / 3100); // Convert to pence and divide by kWh

  const displayCategoryId = activeCategoryId || defaultCategoryId;
  const displaySegment = activeSegment || defaultSegment;

  const chartDimensions = getChartDimensions();

  return (
    <div className="App">
      <div className="dashboard">
        <div className="chart-card">
          <h1 className="chart-title">Breakdown</h1>
          <div className="chart-subtitle">
            Benchmark 3100 kWh household
            <span
              className="benchmark-info-icon"
              onMouseEnter={() => setShowBenchmarkInfo(true)}
              onMouseLeave={() => setShowBenchmarkInfo(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
            {showBenchmarkInfo && (
              <span className="benchmark-info-modal">
                The OFGEM Typical Domestic Consumption Value is currently 2700 kWh for a profile class 1 household, and 3900 kWh for a profile class 2 household. Around 82% of households are class 1.
                <br/><br/>
                This analysis uses 3100 kWh as a benchmark since it is close to the weighted average of these, with a little added for electrification. It also enables direct comparison with OFGEM's previous price caps, which are calculated for a nominal 3100 kWh household.
              </span>
            )}
          </div>
          <div className="year-selector">
            <button
              className="year-nav prev"
              onClick={() => handleYearChange('prev')}
              disabled={availableYears.indexOf(selectedYear) === 0}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="years-container" ref={yearsContainerRef}>
              {availableYears.map(year => (
                <div
                  key={year}
                  className={`year-item ${year === selectedYear ? 'active' : ''}`}
                  onClick={() => setSelectedYear(year)}
                >
                  {year === '26 FYTD' ? 2026 : 2000 + Number(year.slice(3, 5))}
                </div>
              ))}
            </div>
            <button
              className="year-nav next"
              onClick={() => handleYearChange('next')}
              disabled={availableYears.indexOf(selectedYear) === availableYears.length - 1}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {selectedYear === '26 FYTD' && (
              <div className="ytd-indicator">(fiscal year to date)</div>
            )}
          </div>
          <div className="chart-container" ref={chartContainerRef}>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart margin={{ top: 15, right: 10, bottom: 10, left: 10 }}>
                <Tooltip content={() => null} />
                <rect
                  x={0}
                  y={0}
                  width="100%"
                  height="100%"
                  fill="transparent"
                  onClick={handleBackgroundClick}
                  style={{ margin: 0, padding: 0 }}
                />
                <Pie
                  data={pieData}
                  innerRadius={chartDimensions.innerRadius}
                  outerRadius={chartDimensions.outerRadius}
                  paddingAngle={1}
                  strokeWidth={10}
                  stroke="transparent"
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                  onMouseEnter={(_, index) => handleSegmentHover({ payload: pieData[index] })}
                  onMouseLeave={() => handleSegmentHover(null)}
                  onClick={(_, index) => handleSegmentClick({ payload: pieData[index] })}
                  isAnimationActive={false}
                  label={(props) => {
                    const { cx, cy, midAngle, outerRadius, value, name } = props;
                    const isActive = activeSegment && activeSegment.name === name;
                    if (!isActive || isMobile) return null;

                    const RADIAN = Math.PI / 180;
                    const radius = outerRadius + 12;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);

                    return (
                      <text
                        x={x}
                        y={y}
                        fill="white"
                        fontSize="13"
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline="middle"
                        style={{
                          filter: 'drop-shadow(0px 0px 2px rgba(0,0,0,0.5))',
                          opacity: 1,
                          transition: 'opacity 0.2s ease-in-out'
                        }}
                      >
                        £{Math.round(value)}
                      </text>
                    );
                  }}
                >
                  {pieData.map((entry) => {
                    const isActive = activeSegment && activeSegment.id === entry.id;
                    // Only apply scale effect if the category has subcategories
                    const hasSubcategories = categories[entry.categoryId].subcategories !== null;
                    const scale = isActive && hasSubcategories ? 'scale(1.02)' : 'scale(1)';

                    return (
                      <Cell
                        key={entry.id}
                        fill={entry.color}
                        opacity={displayCategoryId && displayCategoryId !== entry.categoryId ? 0.3 : 1}
                        style={{ transform: scale }}
                      />
                    );
                  })}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="center-text">
              <div className="amount">£{Math.round(totalBill)}</div>
              <div className="rate">
                {ratePerKwh}p / kWh
                <span className="info-icon">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="8" r="1" fill="currentColor"/>
                    <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <div className="tooltip-content">
                    This is an all-inclusive pence per kilowatt hour. It is the total cost divided by the usage of a benchmark 3100 kWh household.
                    <br/>
                    The marginal £ / kWh will be roughly 20% lower than this due to fixed standing charges.
                  </div>
                </span>
              </div>
              {isMobile && displaySegment && (
                <div className="center-segment-value" style={{ color: displaySegment.color }}>£{Math.round(displaySegment.value)}</div>
              )}
            </div>
          </div>
          <div className="legend">
            {legendGroups.map((group) => (
              <div
                key={group.id}
                className={`legend-item ${displayCategoryId === group.id ? 'active' : ''}`}
                style={{ color: group.color }}
                onMouseEnter={() => handleLegendHover(group.id)}
                onMouseLeave={() => handleLegendHover(null)}
              >
                <div className="legend-color" style={{ backgroundColor: group.color }}></div>
                <span>{group.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="info-card">
          <div className="info-card-inner">
            {displaySegment ? (
              <>
                <h2>{displaySegment.name}</h2>
                {(displaySegment.subtitle || (categories[displaySegment.categoryId]?.subcategories?.[displaySegment.id.split('-')[1]]?.subtitle)) && (
                  <div className="subtitle">
                    {displaySegment.subtitle || categories[displaySegment.categoryId]?.subcategories?.[displaySegment.id.split('-')[1]]?.subtitle}
                  </div>
                )}
                <div className="description-container">
                  <p className="description" dangerouslySetInnerHTML={{ __html: displaySegment.description }}></p>
                </div>
                {renderHistoricalChart()}
              </>
            ) : (
              <div className="hover-instruction">Hover over chart</div>
            )}
          </div>
        </div>
      </div>
      <div className="footer">
        Data from OFGEM, NESO, Elexon, DNOs. Estimates only.{' '}
        <a href="https://docs.google.com/spreadsheets/d/1SHuFAtxxKUf3Q2b7usoTl8rwngv9YyFkunNVrwqqbAo/edit" target="_blank" rel="noopener noreferrer">
          Data and methodology
          <svg className="external-link-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>.
        <br />
        By <a href="https://benjames.io" target="_blank" rel="noreferrer">Ben James</a> (newsletter <a href="https://benbyfax.substack.com/subscribe" target="_blank" rel="noreferrer">here</a>)
      </div>
    </div>
  );
}

export default Dashboard;
